import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './components/Home';
import About from './components/About';
import Login from './components/Login';
import NotFound from './components/NotFound';
import AuthProvider, { useAuth } from './components/AuthContext.js';
import PageLayout from './components/PageLayout.js';
import ErrorBoundary from './components/ErrorBoundary';

const Dashboard     = lazy(() => import('./components/Dashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const OwnerDashboard = lazy(() => import('./components/OwnerDashboard'));
const EditStudent   = lazy(() => import('./components/EditStudent'));
const LibraryDetails = lazy(() => import('./components/LibraryDetails'));
const Analytics     = lazy(() => import('./components/Analytics'));
const Libraries     = lazy(() => import('./components/LIbraries'));
const WhyJoin       = lazy(() => import('./components/WhyJoin'));
const PrivacyPolicy = lazy(() => import('./components/privacy-policy'));
const TermsOfService = lazy(() => import('./components/terms-of-service'));
const ManageOffers  = lazy(() => import('./components/ManageOffers'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-900 to-purple-900">
    <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <ErrorBoundary>
          <PageLayout>
            <Navbar />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/login" element={<Login />} />
                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/admin-dashboard" element={<PrivateRoute role="admin"><AdminDashboard /></PrivateRoute>} />
                <Route path="/owner-dashboard" element={<PrivateRoute role="owner"><OwnerDashboard /></PrivateRoute>} />
                <Route path="/edit-user/:userId" element={<PrivateRoute role={['admin', 'owner']}><EditStudent /></PrivateRoute>} />
                <Route path="/library/:libraryId" element={<LibraryDetails />} />
                <Route path="/analytics" element={<PrivateRoute role={['admin', 'owner']}><Analytics /></PrivateRoute>} />
                <Route path="/libraries" element={<Libraries />} />
                <Route path="/why-join" element={<WhyJoin />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/offers" element={<PrivateRoute role={['admin', 'owner']}><ManageOffers /></PrivateRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PageLayout>
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

const PrivateRoute = ({ children, role }) => {
  const { currentUser, role: userRole } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (role && (Array.isArray(role) ? !role.includes(userRole) : role !== userRole)) {
    return <Navigate to="/" />;
  }

  return children;
};

export default App;
